<template lang="pug">
  b-container.px-3.px-md-4.py-3(fluid)
    div.d-flex.justify-content-between.mb-2
      div.device-header
        span {{$t('mdm_page.app_management')}} ({{filteredApps.length}})
    div.list-toolbar.mb-2
      b-form-input.list-toolbar__search(
        v-model="searchKeyword",
        :placeholder="searchPlaceholder",
        size="sm",
        @keyup.enter="page.current = 1")
      b-btn(size="sm", variant="secondary", @click="page.current = 1") {{$t('search')}}
      b-btn(size="sm", variant="primary", @click="openUpload")
        icon(name="plus")
        span.ml-1 {{$t('mdm_page.add_app')}}
    b-card.table-card
      b-table.list-stacked(
        :fields="fields",
        :items="pagedApps",
        show-empty,
        :empty-text="$t('mdm_page.no_app')",
        hover,
        stacked="md")
        template(v-slot:cell(identifier)="row")
          code {{row.item.identifier}}
        template(v-slot:cell(format)="row")
          span(v-if="row.item.file_name") {{fileFormat(row.item)}}
          span.text-muted(v-else) -
        template(v-slot:cell(actions)="row")
          b-btn(size="sm", variant="link", @click="openInstallPolicy(row.item)") {{$t('mdm_page.config')}}
          span.text-muted ·
          b-btn(size="sm", variant="link", @click="removeApp(row.item)") {{$t('delete')}}
    div.pagination-wrap
      b-form-select(size="sm", v-model="page.per", :options="page.perOptions", style="width: auto")
      b-pagination.m-0(size="sm", v-model="page.current", :total-rows="filteredApps.length", :per-page="page.per")
    mdm-app-upload-modal(ref="uploadModal", @uploaded="fetchApps")
    mdm-app-install-policy-modal(ref="policyModal", @policy-saved="fetchApps")
</template>

<script>
// 앱 목록 — 플랫폼별(windows/android/osx). 라우트 :platform 파라미터로 구분
import _ from "lodash"
import { MdmAppAPI } from "@/api/mdm"
import MdmAppUploadModal from "./MdmAppUploadModal.vue"
import MdmAppInstallPolicyModal from "./MdmAppInstallPolicyModal.vue"

export default {
  name: "MdmAppPage",
  components: {
    "mdm-app-upload-modal": MdmAppUploadModal,
    "mdm-app-install-policy-modal": MdmAppInstallPolicyModal,
  },
  data() {
    return {
      apps: [],
      searchKeyword: "",
      page: { current: 1, per: 20, perOptions: [10, 20, 50] },
    }
  },
  computed: {
    platform() {
      return this.$route.params.platform // windows / android / osx
    },
    searchPlaceholder() {
      return this.platform === "android"
        ? this.$t("mdm_page.package_id_search")
        : this.$t("mdm_page.app_name_search")
    },
    fields() {
      // 플랫폼별 컬럼 차이: Android만 패키지 ID(identifier) 노출
      const leadingColumns =
        this.platform === "android" ? [{ key: "identifier", label: this.$t("mdm_page.package_id") }] : []
      // 형식(EXE/MSI/PKG/DMG): Android은 패키지 ID로 식별하므로 제외, Windows·macOS만 노출
      const formatColumn =
        this.platform === "android" ? [] : [{ key: "format", label: this.$t("mdm_page.format") }]
      return [
        ...leadingColumns,
        { key: "app_name", label: this.$t("mdm_page.app_name"), sortable: true },
        { key: "version", label: this.$t("version") },
        { key: "file_name", label: this.$t("mdm_page.install_file") },
        ...formatColumn,
        { key: "actions", label: this.$t("mdm_page.actions"), thStyle: { width: "160px" } },
      ]
    },
    filteredApps() {
      if (!this.searchKeyword) return this.apps
      const keyword = this.searchKeyword.toUpperCase()
      return _.filter(
        this.apps,
        app =>
          (app.app_name || "").toUpperCase().includes(keyword) ||
          (app.identifier || "").toUpperCase().includes(keyword),
      )
    },
    pagedApps() {
      const offset = this.page.per * (this.page.current - 1)
      return _.slice(this.filteredApps, offset, offset + this.page.per)
    },
  },
  methods: {
    async fetchApps() {
      try {
        const response = await MdmAppAPI.get("", { params: { platform: this.platform } })
        this.apps = response.data.objects || []
      } catch (error) {
        this.apps = []
      }
    },
    // 설치 파일 확장자로 형식 표기(EXE/MSI/PKG/DMG) — 백엔드 응답엔 형식 필드가 없어 파일명에서 도출
    fileFormat(app) {
      const ext = (app.file_name || "").split(".").pop()
      return ext ? ext.toUpperCase() : "-"
    },
    openUpload() {
      this.$refs.uploadModal.open(this.platform)
    },
    // 앱 설치 정책 모달 — 이 앱을 각 구성에 어떻게 적용할지 설정
    openInstallPolicy(app) {
      this.$refs.policyModal.open(this.platform, app)
    },
    // 앱 삭제 — 참조 중인 구성이 있으면 409(전역 인터셉터가 "구성에서 먼저 제거" 안내)
    removeApp(app) {
      const dialogParams = {
        context: {
          title: this.$t("mdm_page.delete_app"),
          path: "mdm_page.confirm_delete_app",
          args: { name: app.app_name },
        },
        withoutInput: true,
        submit: { size: "sm", color: "danger", text: "delete" },
      }
      this.AppState.$emit("show-confirm-dialog", dialogParams, async action => {
        if (action !== "submit") return
        try {
          await MdmAppAPI.delete(`${app.pk}`)
          this.$awn.success(this.$t("mdm_page.app_deleted"))
          await this.fetchApps()
        } catch (error) {
          // 참조 구성 있으면 409 — 전역 인터셉터가 처리
        }
      })
    },
  },
  watch: {
    "$route.params.platform"() {
      // 플랫폼 탭 전환 시 초기화 후 재조회
      this.page.current = 1
      this.searchKeyword = ""
      void this.fetchApps()
    },
  },
  created() {
    void this.fetchApps()
  },
}
</script>

<style scoped lang="scss">
.device-header {
  padding-bottom: 0.5rem;
  font-size: 1.2rem;
  font-weight: 500;
  color: #000000;
}
</style>
